import { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Tag, FileText, User, Calendar, Bug, Sparkles, Wrench, Zap, Loader2, AlertCircle, Plus, X } from 'lucide-react';
import { releaseApi, ReleaseDetail, ReleaseDetailCreate } from '../../services/releaseApi';

const PAGE_SIZE = 5;

const RELEASE_TYPE_CONFIG: Record<string, { label: string; icon: typeof Bug; color: string }> = {
  bugfix: { label: 'Bug Fix', icon: Bug, color: 'bg-red-100 text-red-700' },
  feature: { label: 'New Feature', icon: Sparkles, color: 'bg-blue-100 text-blue-700' },
  improvement: { label: 'Improvement', icon: Wrench, color: 'bg-yellow-100 text-yellow-700' },
  hotfix: { label: 'Hotfix', icon: Zap, color: 'bg-orange-100 text-orange-700' },
};

function formatDate(dateStr: string | null): string {
  if (!dateStr) return 'N/A';
  const date = new Date(dateStr);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function ReleaseNotesPanel() {
  const [releases, setReleases] = useState<ReleaseDetail[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<string>('');
  const [expandedId, setExpandedId] = useState<number | null>(null);

  // Create form state
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [newRelease, setNewRelease] = useState<ReleaseDetailCreate>({
    release_number: '',
    description: '',
    release_type: 'feature',
    author: '',
    tags: [],
  });
  const [tagInput, setTagInput] = useState('');

  const handleCreateRelease = async () => {
    if (!newRelease.release_number.trim() || !newRelease.description.trim()) {
      setCreateError('Release number and description are required');
      return;
    }
    setCreating(true);
    setCreateError(null);
    try {
      await releaseApi.createRelease({
        ...newRelease,
        author: newRelease.author?.trim() || undefined,
        tags: newRelease.tags && newRelease.tags.length > 0 ? newRelease.tags : undefined,
      });
      setShowCreateForm(false);
      setNewRelease({ release_number: '', description: '', release_type: 'feature', author: '', tags: [] });
      setTagInput('');
      setOffset(0);
      fetchReleases();
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create release');
    } finally {
      setCreating(false);
    }
  };

  const fetchReleases = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await releaseApi.getReleases({
        release_type: filterType || undefined,
        limit: PAGE_SIZE,
        offset,
      });
      setReleases(response.releases);
      setTotal(response.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load releases');
      setReleases([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [offset, filterType]);

  useEffect(() => {
    fetchReleases();
  }, [fetchReleases]);

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  const goToPage = (page: number) => {
    setOffset((page - 1) * PAGE_SIZE);
    setExpandedId(null);
  };

  const handleFilterChange = (type: string) => {
    setFilterType(type);
    setOffset(0);
    setExpandedId(null);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">Release Notes</h3>
          <p className="text-sm text-gray-500 mt-0.5">
            {total} release{total !== 1 ? 's' : ''} recorded
          </p>
        </div>
        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="flex items-center gap-1 px-3 py-1.5 text-sm bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
        >
          {showCreateForm ? <X size={14} /> : <Plus size={14} />}
          {showCreateForm ? 'Cancel' : 'Add Release'}
        </button>
      </div>

      {/* Create Release Form */}
      {showCreateForm && (
        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 space-y-3">
          {createError && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
              {createError}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Release Number *</label>
              <input
                type="text"
                placeholder="e.g. 1.0.0"
                value={newRelease.release_number}
                onChange={(e) => setNewRelease({ ...newRelease, release_number: e.target.value })}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-300 focus:border-primary-400 outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
              <select
                value={newRelease.release_type}
                onChange={(e) => setNewRelease({ ...newRelease, release_type: e.target.value })}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-300 focus:border-primary-400 outline-none"
              >
                {Object.entries(RELEASE_TYPE_CONFIG).map(([key, config]) => (
                  <option key={key} value={key}>{config.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Description *</label>
            <textarea
              placeholder="Describe what changed in this release..."
              value={newRelease.description}
              onChange={(e) => setNewRelease({ ...newRelease, description: e.target.value })}
              rows={3}
              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-300 focus:border-primary-400 outline-none resize-none"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Author</label>
              <input
                type="text"
                placeholder="Author name"
                value={newRelease.author || ''}
                onChange={(e) => setNewRelease({ ...newRelease, author: e.target.value })}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-300 focus:border-primary-400 outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Tags (press Enter to add)</label>
              <div className="flex gap-1 flex-wrap items-center border border-gray-300 rounded-lg px-2 py-1 focus-within:ring-2 focus-within:ring-primary-300 focus-within:border-primary-400">
                {newRelease.tags?.map((tag, idx) => (
                  <span key={idx} className="inline-flex items-center gap-0.5 text-xs bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded">
                    {tag}
                    <button
                      onClick={() => setNewRelease({ ...newRelease, tags: newRelease.tags?.filter((_, i) => i !== idx) })}
                      className="hover:text-red-500"
                    >
                      <X size={10} />
                    </button>
                  </span>
                ))}
                <input
                  type="text"
                  placeholder={newRelease.tags?.length ? '' : 'Add tag...'}
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && tagInput.trim()) {
                      e.preventDefault();
                      setNewRelease({ ...newRelease, tags: [...(newRelease.tags || []), tagInput.trim()] });
                      setTagInput('');
                    }
                  }}
                  className="flex-1 min-w-[60px] text-sm py-0.5 outline-none bg-transparent"
                />
              </div>
            </div>
          </div>
          <div className="flex justify-end">
            <button
              onClick={handleCreateRelease}
              disabled={creating}
              className="flex items-center gap-1 px-4 py-1.5 text-sm bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50"
            >
              {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              {creating ? 'Creating...' : 'Create Release'}
            </button>
          </div>
        </div>
      )}

      {/* Type Filter */}
      <div className="flex gap-2 flex-wrap">
        <button
          onClick={() => handleFilterChange('')}
          className={`px-3 py-1 text-xs rounded-full border transition-colors ${
            filterType === ''
              ? 'bg-gray-800 text-white border-gray-800'
              : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
          }`}
        >
          All
        </button>
        {Object.entries(RELEASE_TYPE_CONFIG).map(([key, config]) => {
          const Icon = config.icon;
          return (
            <button
              key={key}
              onClick={() => handleFilterChange(key)}
              className={`px-3 py-1 text-xs rounded-full border transition-colors flex items-center gap-1 ${
                filterType === key
                  ? 'bg-gray-800 text-white border-gray-800'
                  : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
              }`}
            >
              <Icon size={12} />
              {config.label}
            </button>
          );
        })}
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-12 text-gray-400">
          <Loader2 size={24} className="animate-spin mr-2" />
          Loading releases...
        </div>
      ) : error ? (
        <div className="flex items-center justify-center py-12 text-red-500">
          <AlertCircle size={20} className="mr-2" />
          {error}
        </div>
      ) : releases.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <FileText size={32} className="mx-auto mb-2 opacity-50" />
          <p className="text-sm">No releases found</p>
          {filterType && (
            <p className="text-xs mt-1">
              Try clearing the filter to see all releases
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {releases.map((release) => {
            const typeConfig = RELEASE_TYPE_CONFIG[release.release_type] || RELEASE_TYPE_CONFIG.bugfix;
            const TypeIcon = typeConfig.icon;
            const isExpanded = expandedId === release.id;

            return (
              <div
                key={release.id}
                className="border border-gray-200 rounded-lg overflow-hidden hover:border-gray-300 transition-colors"
              >
                {/* Release Header - clickable to expand */}
                <button
                  onClick={() => setExpandedId(isExpanded ? null : release.id)}
                  className="w-full flex items-start gap-3 p-3 text-left hover:bg-gray-50 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-gray-800 text-sm">
                        v{release.release_number}
                      </span>
                      <span
                        className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${typeConfig.color}`}
                      >
                        <TypeIcon size={10} />
                        {typeConfig.label}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                      {release.description}
                    </p>
                    <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-400">
                      <span className="flex items-center gap-1">
                        <Calendar size={11} />
                        {formatDate(release.created_at)}
                      </span>
                      {release.author && (
                        <span className="flex items-center gap-1">
                          <User size={11} />
                          {release.author}
                        </span>
                      )}
                      {release.files_changed && release.files_changed.length > 0 && (
                        <span className="flex items-center gap-1">
                          <FileText size={11} />
                          {release.files_changed.length} file{release.files_changed.length !== 1 ? 's' : ''} changed
                        </span>
                      )}
                    </div>
                  </div>
                  <ChevronRight
                    size={16}
                    className={`text-gray-400 transition-transform mt-1 flex-shrink-0 ${
                      isExpanded ? 'rotate-90' : ''
                    }`}
                  />
                </button>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="border-t border-gray-100 bg-gray-50 p-3 space-y-3">
                    {/* Full description */}
                    <div>
                      <h4 className="text-xs font-medium text-gray-500 uppercase mb-1">Description</h4>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{release.description}</p>
                    </div>

                    {/* Files Changed */}
                    {release.files_changed && release.files_changed.length > 0 && (
                      <div>
                        <h4 className="text-xs font-medium text-gray-500 uppercase mb-1">Files Changed</h4>
                        <div className="bg-white border border-gray-200 rounded p-2 max-h-40 overflow-y-auto">
                          {release.files_changed.map((file, idx) => (
                            <div key={idx} className="text-xs font-mono text-gray-600 py-0.5">
                              {file}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Tags */}
                    {release.tags && release.tags.length > 0 && (
                      <div>
                        <h4 className="text-xs font-medium text-gray-500 uppercase mb-1">Tags</h4>
                        <div className="flex gap-1 flex-wrap">
                          {release.tags.map((tag, idx) => (
                            <span
                              key={idx}
                              className="inline-flex items-center gap-1 text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded"
                            >
                              <Tag size={10} />
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2 border-t border-gray-100">
          <p className="text-xs text-gray-500">
            Showing {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage <= 1}
              className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed text-gray-600"
              title="Previous page"
            >
              <ChevronLeft size={16} />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => goToPage(page)}
                className={`w-7 h-7 rounded text-xs font-medium transition-colors ${
                  page === currentPage
                    ? 'bg-primary-500 text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {page}
              </button>
            ))}
            <button
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage >= totalPages}
              className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed text-gray-600"
              title="Next page"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
